import { cx } from '@linaria/core';
import { useQuery } from '@tanstack/react-query';
import { memo, useRef, useState, useEffect, useCallback, Suspense } from 'react';
import { useLeaveRevision, useCurrentCourseDescriptor } from '@pm2/course-revisions';
import { NotificationCount } from '@pm2/react-utils';
import { getStatusForProductQuery, useGetNotificationsQuery, isNotificationConsideredRecent, UserArea } from '@pm2/user-area';
import { useAppContext } from '@provider/app-context';
import { breakpoints, resets } from '@ui/css';
import { Tooltip, UserIcon, PublisherLogo } from '@ui/kit';
import { useMatchBreakpoint, useForceHeaderExpanded, IsomorphicPortal } from '@utils/react';
import { Snackbar } from './Snackbar/Snackbar.js';
import { guideBubbleWrapperCls, toolButtonCls } from './root.tools.style.js';
import { notificationBadge, buttonCls, backdropCls, userAreaWrapperCls } from './tool.user-area.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { headerSelector } from '../Header.js';

const UserAreaToolNonMemoized = ({
  enabled,
  onEnable,
  onDisable
}) => {
  const productId = useAppContext(state => state.product?.isbn);
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const ref = useRef(null);
  const panelRef = useRef(null);
  const [urgentNotifications, setUrgentNotifications] = useState(undefined);
  const status = useQuery(getStatusForProductQuery(productId));
  const notifications = useGetNotificationsQuery();
  const notificationCount = (status.data?.length ?? 0) + (notifications.data?.filter(it => it.type !== "dialog" && isNotificationConsideredRecent(it)).length ?? 0);
  const leaveRevision = useLeaveRevision(useCurrentCourseDescriptor());

  // Ensure that the UserArea is automatically closed in case the header is
  // clicked. The header is the only other interactable element when the
  // userarea is open)
  useEffect(() => {
    const header = document.querySelector(headerSelector);
    if (!enabled || !header) {
      return;
    }
    const handleClick = evt => {
      if (ref.current === evt.target || ref.current?.contains(evt.target)) {
        // don't double-handle click on the user area button itself
        return;
      }
      onDisable();
    };
    header.addEventListener("click", handleClick);
    return () => {
      header.removeEventListener("click", handleClick);
    };
  }, [enabled, onDisable]);
  useForceHeaderExpanded(!isMobile && enabled);
  const handleClick = async evt => {
    // toggle on/off on click
    if (!enabled) {
      const intent = await leaveRevision();
      if (intent === "cancel") {
        return;
      }
      onEnable();
    } else {
      onDisable();
    }
    evt.stopPropagation();
  };
  const onUrgentNotifications = useCallback(notifications => {
    setUrgentNotifications(notifications);
  }, []);
  const showNotifications = notificationCount > 0;
  return /*#__PURE__*/jsxs("div", {
    className: guideBubbleWrapperCls,
    children: [/*#__PURE__*/jsx(Tooltip, {
      containerRef: ref,
      placement: "bottom",
      offsetPx: 0,
      body: "Min profil: besvarelser, mine forl\xF8b og indstillinger",
      ariaType: "label",
      disabled: enabled,
      children: ref => /*#__PURE__*/jsxs("button", {
        ref: ref,
        className: cx(toolButtonCls, buttonCls),
        onClickCapture: handleClick,
        children: [/*#__PURE__*/jsx("span", {
          children: /*#__PURE__*/jsx(UserIcon, {
            height: 22
          })
        }), /*#__PURE__*/jsx(NotificationCount, {
          className: notificationBadge,
          active: showNotifications
        })]
      })
    }), /*#__PURE__*/jsx(IsomorphicPortal, {
      children: /*#__PURE__*/jsx(Suspense, {
        fallback: null,
        children: /*#__PURE__*/jsxs("div", {
          className: cx(resets.rootCls, userAreaWrapperCls),
          ref: panelRef,
          children: [/*#__PURE__*/jsx(UserArea, {
            requestedActive: enabled,
            active: enabled,
            backdropIllustration: /*#__PURE__*/jsx(PublisherLogo, {
              color: "#F5F5F5",
              small: true
            }),
            onClose: onDisable,
            onUrgentNotifications: onUrgentNotifications
          }), /*#__PURE__*/jsx("div", {
            className: cx(backdropCls, enabled && "active"),
            role: "presentation"
          })]
        })
      })
    }), /*#__PURE__*/jsx(Snackbar, {
      notifications: urgentNotifications
    })]
  });
};
const UserAreaTool = /*#__PURE__*/memo(UserAreaToolNonMemoized);

export { UserAreaTool };
//# sourceMappingURL=tool.user-area.js.map
